import { useState, useEffect, useRef } from 'react';
import { goBack } from '../utils/navHistory';
import TripAlertsCenter from './TripAlertsCenter';

function getIndiaDateTime() {
    const now = new Date();
    const options = {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    };
    const dateOptions = {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    };
    const time = now.toLocaleTimeString('en-IN', options);
    const date = now.toLocaleDateString('en-IN', dateOptions);
    return `${time} · ${date}`;
}

export default function Topbar({ isDark, onToggleTheme, onLogout, user, operatorDetails, onNavigate, onToggleSidebar }) {
    const [dateTime, setDateTime] = useState(getIndiaDateTime());
    const [notifications, setNotifications] = useState([]);
    const [showNotifs, setShowNotifs] = useState(false);
    const [showNotStarted, setShowNotStarted] = useState(false);
    const [expiredDrivers, setExpiredDrivers] = useState([]);
    const [showExpiredModal, setShowExpiredModal] = useState(false);
    
    // Driver Daily Login Status
    const [driverDailyStatus, setDriverDailyStatus] = useState({
        total: 0,
        onlineCount: 0,
        notLoggedInCount: 0,
        online: [],
        notLoggedIn: []
    });
    const [showDriverModal, setShowDriverModal] = useState(false);
    const [driverModalTab, setDriverModalTab] = useState('online'); // 'online' or 'notLoggedIn'
    const [driverSearchQuery, setDriverSearchQuery] = useState('');

    const isOperator = user?.userType === 'operator';
    const operatorId = (typeof user?.operatorId === 'object' ? user?.operatorId?._id : user?.operatorId) || (user?.userType === 'operator' ? user._id : null);
    const isSuperadmin = user?.userType === 'admin';

    const driverStatusInFlight = useRef(false);

    const loadDriverStatus = async () => {
        if (!operatorId && !isSuperadmin) return;
        // Skip if the previous poll is still running (avoids piling up requests)
        if (driverStatusInFlight.current) return;
        driverStatusInFlight.current = true;
        try {
            await fetchDriverStatus();
        } finally {
            driverStatusInFlight.current = false;
        }
    };

    const fetchDriverStatus = async () => {
        const op = isSuperadmin ? 'all' : operatorId;

        // Try dedicated daily status API first
        try {
            const res = await fetch(`/api/drivers-daily-status/${op}`, { cache: 'no-store' });
            if (res.ok) {
                const data = await res.json();
                if (data && typeof data.onlineCount === 'number' && (data.total > 0 || data.onlineCount > 0 || data.notLoggedInCount > 0)) {
                    setDriverDailyStatus(data);
                    return;
                }
            }
        } catch (e) {
            // Fall through to standard drivers fetch
        }

        // Fallback: Fetch standard drivers list and compute today's status
        try {
            const drvUrl = isSuperadmin ? `/api/drivers/all` : `/api/drivers/${op}`;
            const res = await fetch(drvUrl, { cache: 'no-store' });
            if (!res.ok) return;
            const drivers = await res.json();
            
            if (Array.isArray(drivers)) {
                const d = new Date();
                const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                
                // Active or non-terminated drivers
                const activeDrivers = drivers.filter(drv => !drv.status || drv.status === 'Active' || drv.status === 'Under Review');
                
                const online = [];
                const notLoggedIn = [];

                activeDrivers.forEach(driver => {
                    let isOnline = false;
                    if (driver.lastLoginAt) {
                        try {
                            const itemDate = new Date(driver.lastLoginAt);
                            const itemDateStr = `${itemDate.getFullYear()}-${String(itemDate.getMonth() + 1).padStart(2, '0')}-${String(itemDate.getDate()).padStart(2, '0')}`;
                            if (itemDateStr === todayStr || itemDate.toISOString().split('T')[0] === todayStr) {
                                isOnline = true;
                            }
                        } catch(e) {}
                    }

                    const driverItem = {
                        _id: driver._id,
                        name: driver.name || 'Unnamed Driver',
                        driverCode: driver.driverCode || driver.driverId || (driver._id ? driver._id.toString().substring(driver._id.toString().length - 6).toUpperCase() : '-'),
                        phone: driver.countryCode && driver.phoneNumber ? `${driver.countryCode} ${driver.phoneNumber}` : (driver.phoneNumber || driver.altPhone || '-'),
                        status: driver.status || 'Active',
                        lastLoginAt: driver.lastLoginAt || null,
                        startOdometer: driver.odometerNumber || 0,
                        endOdometer: 0,
                        operatorName: driver.operatorId?.companyName || '-'
                    };

                    if (isOnline) {
                        online.push(driverItem);
                    } else {
                        notLoggedIn.push(driverItem);
                    }
                });

                setDriverDailyStatus({
                    date: todayStr,
                    total: activeDrivers.length,
                    onlineCount: online.length,
                    notLoggedInCount: notLoggedIn.length,
                    online,
                    notLoggedIn
                });
            }
        } catch (err) {
            console.error('Error loading fallback driver status:', err);
        }
    };

    const loadNotifications = () => {
        if (isSuperadmin) {
            fetch('/api/tickets')
                .then(r => r.json())
                .then(data => {
                    const superadminTickets = data.filter(t => t.tags?.includes('To Superadmin') && t.status?.name !== 'Closed' && t.status?.name !== 'Resolved');
                    if (superadminTickets.length > 0) {
                        setNotifications([{
                            _id: 'superadmin-tickets-notif',
                            isRead: false,
                            title: 'Support Tickets Awaiting Response',
                            message: `You have ${superadminTickets.length} open ticket(s) from operators.`,
                            createdAt: new Date().toISOString(),
                            type: 'superadmin_ticket'
                        }]);
                    } else {
                        setNotifications([]);
                    }
                })
                .catch(err => console.error('Error loading tickets for notifs:', err));
        } else if (operatorId) {
            fetch(`/api/notifications/operator/${operatorId}`)
                .then(r => r.json())
                .then(data => setNotifications(Array.isArray(data) ? data : []))
                .catch(err => console.error('Error loading notifications:', err));
        }

        // Load expired drivers, vehicles, and client contracts/documents
        if (operatorId || isSuperadmin) {
            const drvUrl = isSuperadmin ? `/api/drivers/all` : `/api/drivers/${operatorId}`;
            const vehUrl = isSuperadmin ? `/api/vehicles/${operatorId === null ? 'all' : operatorId}` : `/api/vehicles/${operatorId}`;
            const cliUrl = isSuperadmin ? `/api/clients/all` : `/api/clients/${operatorId}`;
            
            Promise.all([
                fetch(drvUrl).then(r => r.json()).catch(() => []),
                fetch(vehUrl).then(r => r.json()).catch(() => []),
                fetch(cliUrl).then(r => r.json()).catch(() => [])
            ]).then(([drivers, vehicles, clients]) => {
                const d = new Date();
                const todayStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                
                const expired = [];
                
                if (Array.isArray(drivers)) {
                    drivers.forEach(driver => {
                        const expList = [];
                        if (driver.civilIdExpiry && String(driver.civilIdExpiry).split('T')[0] < todayStr) expList.push('Civil ID');
                        if (driver.passportExpiry && String(driver.passportExpiry).split('T')[0] < todayStr) expList.push('Passport');
                        if (driver.licenseExpiry && String(driver.licenseExpiry).split('T')[0] < todayStr) expList.push('Driving License');
                        
                        if (expList.length > 0) {
                            expired.push({ 
                                _id: driver._id, 
                                isVehicle: false,
                                isClient: false,
                                displayId: driver.driverCode || driver.driverId || '-',
                                displayName: driver.name || '-',
                                expiredList: expList 
                            });
                        }
                    });
                }
                
                if (Array.isArray(vehicles)) {
                    vehicles.forEach(vehicle => {
                        const expList = [];
                        if (vehicle.insuranceExpiry && String(vehicle.insuranceExpiry).split('T')[0] < todayStr) expList.push('Insurance');
                        if (vehicle.registrationExpiry && String(vehicle.registrationExpiry).split('T')[0] < todayStr) expList.push('Registration');
                        if (vehicle.vehicleLicenseExpiry && String(vehicle.vehicleLicenseExpiry).split('T')[0] < todayStr) expList.push('Vehicle License');
                        
                        if (expList.length > 0) {
                            expired.push({ 
                                _id: vehicle._id, 
                                isVehicle: true,
                                isClient: false,
                                displayId: vehicle.vehicleCode || vehicle.plateNumber || '-',
                                displayName: vehicle.make && vehicle.model ? `${vehicle.make} ${vehicle.model}` : vehicle.plateNumber,
                                expiredList: expList 
                            });
                        }
                    });
                }

                if (Array.isArray(clients)) {
                    clients.forEach(client => {
                        const expList = [];
                        if (Array.isArray(client.contracts)) {
                            client.contracts.forEach(c => {
                                if (c.endDate && String(c.endDate).split('T')[0] < todayStr) {
                                    expList.push(`Contract: ${c.title || 'Agreement'}`);
                                }
                            });
                        }
                        if (Array.isArray(client.documents)) {
                            client.documents.forEach(doc => {
                                if (doc.expiryDate && String(doc.expiryDate).split('T')[0] < todayStr) {
                                    expList.push(`Doc: ${doc.title || doc.type || 'Document'}`);
                                }
                            });
                        }
                        
                        if (expList.length > 0) {
                            expired.push({
                                _id: client._id,
                                isVehicle: false,
                                isClient: true,
                                displayId: client.companyName ? (client.companyName.length > 15 ? client.companyName.substring(0, 15) + '…' : client.companyName) : 'Client',
                                displayName: client.companyName || 'Corporate Client',
                                expiredList: expList
                            });
                        }
                    });
                }
                
                setExpiredDrivers(expired);
            }).catch(err => console.error('Error loading expiry data:', err));
        }
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setDateTime(getIndiaDateTime());
        }, 1000);
        
        loadNotifications();
        loadDriverStatus();
        const notifTimer = setInterval(() => {
            loadNotifications();
        }, 30000);

        // Drivers Logged In / Not Logged In badges: live refresh every 2 seconds
        const driverStatusTimer = setInterval(() => {
            if (document.visibilityState === 'visible') {
                loadDriverStatus();
            }
        }, 2000);
        
        return () => {
            clearInterval(timer);
            clearInterval(notifTimer);
            clearInterval(driverStatusTimer);
        };
    }, [operatorId, isSuperadmin]);

    const unreadCount = notifications.filter(n => !n.isRead).length;

    // "Not Started" chip: live list of today's trips whose driver hasn't started yet
    // (starting within 30 min, or already past start time). Source: /api/not-started-trips
    const [notStarted, setNotStarted] = useState({ count: 0, late: 0, upcoming: 0, trips: [] });
    useEffect(() => {
        if (!isOperator || !operatorId) return;
        let alive = true;
        const loadNotStarted = () => {
            if (document.visibilityState !== 'visible') return;
            fetch(`/api/not-started-trips/${operatorId}?window=30`, { cache: 'no-store' })
                .then(r => (r.ok ? r.json() : null))
                .then(d => { if (alive && d && Array.isArray(d.trips)) setNotStarted(d); })
                .catch(() => {});
        };
        loadNotStarted();
        const t = setInterval(loadNotStarted, 30000);
        return () => { alive = false; clearInterval(t); };
    }, [isOperator, operatorId]);

    const markAllRead = async () => {
        if (!operatorId) return;
        try {
            await fetch(`/api/notifications/mark-all-read/operator/${operatorId}`, { method: 'PUT' });
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
        } catch (err) { console.error(err); }
    };

    const markRead = async (id) => {
        if (isSuperadmin) return; // Superadmin tickets notification is just a computed reflection, we don't 'mark read' permanently here.
        try {
            await fetch(`/api/notifications/${id}/read`, { method: 'PUT' });
            setNotifications(prev => prev.map(n => n._id === id ? { ...n, isRead: true } : n));
        } catch (err) { console.error(err); }
    };

    // Go back to the previously opened page inside the panel.
    // Uses our own in-app history (utils/navHistory) so it always works.
    const handleBack = () => {
        goBack(() => {
            if (onNavigate) onNavigate(isOperator ? 'operator-dashboard' : 'dashboard');
        });
    };

    return (
        <header className={`topbar ${isOperator ? 'operator-topbar' : ''}`}>
            {/* ... previous topbar-left content ... */}
            <div className="topbar-left">
                <button className="topbar-icon-btn" onClick={onToggleSidebar} style={{ border: 'none', background: 'transparent', padding: '8px', marginRight: '8px', color: '#64748b', cursor: 'pointer' }} title="Toggle Sidebar">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
                </button>
                {!isOperator && (
                    <div className="topbar-meta">{dateTime}</div>
                )}
                {isOperator && (
                    <div
                        className="topbar-back-btn"
                        role="button"
                        tabIndex={0}
                        title="Go Back"
                        style={{ cursor: 'pointer', position: 'relative', zIndex: 5 }}
                        onClick={handleBack}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBack(); } }}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
                    </div>
                )}
            </div>

            <div className="topbar-actions" style={{ display: 'flex', alignItems: 'center' }}>
                {(isOperator || isSuperadmin) ? (
                    <>
                        {/* 0. Delayed / Overdue Trip Alerts (CT-32) */}
                        <TripAlertsCenter operatorId={operatorId} isSuperadmin={isSuperadmin} user={user} />

                        {/* 0b. Not Started — today's trips whose driver hasn't started (live) */}
                        {isOperator && notStarted.count > 0 && (
                            <div style={{ position: 'relative', marginRight: 10 }}>
                                <style>{`@keyframes nsPulse { 0% { box-shadow: 0 0 0 0 rgba(220,38,38,.45);} 70% { box-shadow: 0 0 0 9px rgba(220,38,38,0);} 100% { box-shadow: 0 0 0 0 rgba(220,38,38,0);} }`}</style>
                                <button
                                    onClick={() => setShowNotStarted(v => !v)}
                                    title="Today's trips the driver has not started yet"
                                    style={{
                                        background: notStarted.late > 0 ? '#dc2626' : '#fff7ed',
                                        color: notStarted.late > 0 ? '#ffffff' : '#c2410c',
                                        border: `1px solid ${notStarted.late > 0 ? '#dc2626' : '#fed7aa'}`,
                                        padding: '6px 13px 6px 9px', borderRadius: 30, fontSize: 12.5, fontWeight: 700,
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 7,
                                        fontFamily: "'Outfit', sans-serif", animation: notStarted.late > 0 ? 'nsPulse 1.6s infinite' : 'none'
                                    }}
                                >
                                    <span style={{ width: 22, height: 22, borderRadius: '50%', background: notStarted.late > 0 ? 'rgba(255,255,255,.2)' : 'rgba(234,88,12,.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><line x1="10" y1="9" x2="10" y2="15" /><line x1="14" y1="9" x2="14" y2="15" /></svg>
                                    </span>
                                    <span>Not Started</span>
                                    <span style={{ background: notStarted.late > 0 ? '#ffffff' : '#ea580c', color: notStarted.late > 0 ? '#dc2626' : '#ffffff', fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 12, lineHeight: 1 }}>{notStarted.count}</span>
                                </button>
                                {showNotStarted && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 380, background: '#fff', borderRadius: 14, border: '1px solid #fecaca', boxShadow: '0 12px 32px rgba(15,23,42,.15)', zIndex: 1000, overflow: 'hidden', fontFamily: "'Outfit', sans-serif" }}>
                                        <div style={{ padding: '12px 16px', background: '#fef2f2', borderBottom: '1px solid #fecaca', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontWeight: 800, fontSize: 14, color: '#b91c1c' }}>Trips Not Started</span>
                                            <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>{notStarted.late} late · {notStarted.upcoming} starting soon</span>
                                        </div>
                                        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                                            {notStarted.trips.map(t => {
                                                const late = t.kind === 'not_started';
                                                const mins = Math.abs(t.lateMinutes);
                                                const when = late ? `Late by ${t.lateText || `${mins} min`}` : (mins < 1 ? 'Starting now' : `Starts in ${mins} min`);
                                                return (
                                                    <div key={t.key} style={{ padding: '12px 16px', borderBottom: '1px solid #f1f5f9' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
                                                            <div style={{ fontSize: 13.5, fontWeight: 800, color: '#0f172a', lineHeight: 1.35 }}>{t.tripName}</div>
                                                            <span style={{ fontSize: 10.5, fontWeight: 800, padding: '3px 8px', borderRadius: 99, whiteSpace: 'nowrap', background: late ? '#fee2e2' : '#ffedd5', color: late ? '#b91c1c' : '#c2410c' }}>{when}</span>
                                                        </div>
                                                        <div style={{ fontSize: 12.5, color: '#334155', marginTop: 4 }}>
                                                            {t.driver ? <><strong>{t.driver.name}</strong>{t.driver.phone ? ` · ${t.driver.phone}` : ''}</> : <span style={{ color: '#b91c1c', fontWeight: 700 }}>No driver assigned</span>}
                                                            {t.vehicle?.number ? ` · ${t.vehicle.number}` : ''}
                                                        </div>
                                                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 3 }}>Scheduled {t.scheduledStart} · {t.reason}</div>
                                                        {t.driver?.phoneDial && (
                                                            <a href={`tel:${t.driver.phoneDial}`} style={{ display: 'inline-block', marginTop: 6, fontSize: 11.5, fontWeight: 700, color: '#16539C', textDecoration: 'none' }}>Call driver</a>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <button onClick={() => { setShowNotStarted(false); if (onNavigate) onNavigate('overdue-trips'); }}
                                            style={{ width: '100%', padding: '11px 16px', background: '#f8fafc', border: 'none', borderTop: '1px solid #e2e8f0', fontSize: 12.5, fontWeight: 700, color: '#16539C', cursor: 'pointer' }}>
                                            Open Overdue Trips →
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* 1. Drivers Logged In Button */}
                        <button
                            onClick={() => {
                                setDriverModalTab('online');
                                setDriverSearchQuery('');
                                setShowDriverModal(true);
                            }}
                            style={{
                                marginRight: 10,
                                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%)',
                                color: '#059669',
                                border: '1px solid rgba(16, 185, 129, 0.22)',
                                padding: '6px 13px 6px 9px',
                                borderRadius: 30,
                                fontSize: 12.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 8px rgba(16, 185, 129, 0.06)',
                                fontFamily: "'Outfit', sans-serif"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#ecfdf5';
                                e.currentTarget.style.borderColor = '#6ee7b7';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(16, 185, 129, 0.15)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.04) 100%)';
                                e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.22)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(16, 185, 129, 0.06)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            title="View Drivers Logged In Today"
                        >
                            <span style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                background: 'rgba(16, 185, 129, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#059669'
                            }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                    <circle cx="9" cy="7" r="4" />
                                    <polyline points="16 11 18 13 22 9" />
                                </svg>
                            </span>
                            <span>Drivers Logged In</span>
                            <span style={{
                                background: '#10b981',
                                color: '#ffffff',
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '2px 7px',
                                borderRadius: 12,
                                lineHeight: 1,
                                boxShadow: '0 1px 4px rgba(16, 185, 129, 0.35)'
                            }}>
                                {driverDailyStatus.onlineCount}
                            </span>
                        </button>

                        {/* 2. Drivers Not Yet Logged In Button */}
                        <button
                            onClick={() => {
                                setDriverModalTab('notLoggedIn');
                                setDriverSearchQuery('');
                                setShowDriverModal(true);
                            }}
                            style={{
                                marginRight: 12,
                                background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%)',
                                color: '#d97706',
                                border: '1px solid rgba(245, 158, 11, 0.22)',
                                padding: '6px 13px 6px 9px',
                                borderRadius: 30,
                                fontSize: 12.5,
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 7,
                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                boxShadow: '0 2px 8px rgba(245, 158, 11, 0.06)',
                                fontFamily: "'Outfit', sans-serif"
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#fffbeb';
                                e.currentTarget.style.borderColor = '#fcd34d';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(245, 158, 11, 0.15)';
                                e.currentTarget.style.transform = 'translateY(-1px)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'linear-gradient(135deg, rgba(245, 158, 11, 0.08) 0%, rgba(217, 119, 6, 0.04) 100%)';
                                e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.22)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(245, 158, 11, 0.06)';
                                e.currentTarget.style.transform = 'translateY(0)';
                            }}
                            title="View Drivers Not Yet Logged In Today"
                        >
                            <span style={{
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                background: 'rgba(245, 158, 11, 0.15)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#d97706'
                            }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                </svg>
                            </span>
                            <span>Not Logged In</span>
                            <span style={{
                                background: '#f59e0b',
                                color: '#ffffff',
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '2px 7px',
                                borderRadius: 12,
                                lineHeight: 1,
                                boxShadow: '0 1px 4px rgba(245, 158, 11, 0.35)'
                            }}>
                                {driverDailyStatus.notLoggedInCount}
                            </span>
                        </button>

                        {/* 3. Expiry Center Button */}
                        {expiredDrivers.length > 0 && (
                            <button 
                                onClick={() => { setShowExpiredModal(false); if (onNavigate) onNavigate('expiry-command-center'); }}
                                style={{
                                    marginRight: 16,
                                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.04) 100%)',
                                    color: '#dc2626',
                                    border: '1px solid rgba(239, 68, 68, 0.22)',
                                    padding: '6px 14px 6px 10px',
                                    borderRadius: 30,
                                    fontSize: 12.5,
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                    boxShadow: '0 2px 8px rgba(239, 68, 68, 0.06)',
                                    fontFamily: "'Outfit', sans-serif"
                                }}
                                onMouseEnter={(e) => { 
                                    e.currentTarget.style.background = '#fef2f2'; 
                                    e.currentTarget.style.borderColor = '#fca5a5'; 
                                    e.currentTarget.style.boxShadow = '0 4px 14px rgba(239, 68, 68, 0.15)'; 
                                    e.currentTarget.style.transform = 'translateY(-1px)'; 
                                }}
                                onMouseLeave={(e) => { 
                                    e.currentTarget.style.background = 'linear-gradient(135deg, rgba(239, 68, 68, 0.08) 0%, rgba(220, 38, 38, 0.04) 100%)'; 
                                    e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.22)'; 
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(239, 68, 68, 0.06)'; 
                                    e.currentTarget.style.transform = 'translateY(0)'; 
                                }}
                                title="View Expiry Command Center"
                            >
                                <span style={{
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    background: 'rgba(239, 68, 68, 0.12)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#dc2626'
                                }}>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path><line x1="12" y1="9" x2="12" y2="13"></line><line x1="12" y1="17" x2="12.01" y2="17"></line></svg>
                                </span>
                                <span>Expiry Center</span>
                                <span style={{
                                    background: '#ef4444',
                                    color: '#ffffff',
                                    fontSize: 11,
                                    fontWeight: 800,
                                    padding: '2px 7px',
                                    borderRadius: 12,
                                    lineHeight: 1,
                                    boxShadow: '0 1px 4px rgba(239, 68, 68, 0.35)'
                                }}>
                                    {expiredDrivers.length}
                                </span>
                            </button>
                        )}
                        <div 
                            className="topbar-icon-btn" 
                            style={{ position: 'relative', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            onClick={() => setShowNotifs(!showNotifs)}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1e293b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
                            </svg>
                            {unreadCount > 0 && (
                                <span style={{
                                    position: 'absolute', top: -2, right: -2, background: '#ef4444',
                                    color: 'white', fontSize: 10, fontWeight: 900, borderRadius: '50%',
                                    width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: '2px solid white'
                                }}>
                                    {unreadCount > 9 ? '9+' : unreadCount}
                                </span>
                            )}
                        </div>
                    </>
                ) : null}
            </div>

            {showNotifs && (
                <div style={{
                    position: 'absolute', top: 64, right: 20, width: 360, maxHeight: 500,
                    background: 'white', borderRadius: 16, boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    border: '1px solid #e2e8f0', zIndex: 9999, overflow: 'hidden', display: 'flex', flexDirection: 'column'
                }}>
                    <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                        <span style={{ fontWeight: 800, fontSize: 15, color: '#0f172a' }}>Notifications</span>
                        <button 
                            onClick={markAllRead}
                            style={{ background: 'none', border: 'none', color: '#16539C', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                            Mark all read
                        </button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>No notifications yet</div>
                        ) : notifications.map(n => (
                            <div 
                                key={n._id} 
                                onClick={() => {
                                    markRead(n._id);
                                    setShowNotifs(false);
                                    if (onNavigate) {
                                        if (n.type === 'superadmin_ticket') {
                                            onNavigate('tickets');
                                        } else if (n.type === 'allocation_updated') {
                                            onNavigate('passenger-allocation');
                                        } else if (n.type === 'support_ticket_created') {
                                            onNavigate('support-tickets');
                                        } else {
                                            onNavigate('operator-trip-list', 'All');
                                        }
                                    }
                                }}
                                style={{
                                    padding: '16px 20px', borderBottom: '1px solid #f1f5f9',
                                    background: n.isRead ? 'transparent' : '#f0f7ff',
                                    cursor: 'pointer', transition: 'background 0.2s'
                                }}
                            >
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <span style={{ fontSize: 13, fontWeight: 700, color: n.isRead ? '#475569' : '#063A5C' }}>{n.title}</span>
                                    {!n.isRead && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#16539C' }} />}
                                </div>
                                <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.5 }}>{n.message}</div>
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 8 }}>{new Date(n.createdAt).toLocaleString()}</div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {showExpiredModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                }} onClick={() => setShowExpiredModal(false)}>
                    <div style={{
                        background: 'white', borderRadius: 16, padding: 24, width: '90%', maxWidth: 600,
                        maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
                    }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                            <h2 style={{ margin: 0, fontSize: 20, color: '#ef4444', display: 'flex', alignItems: 'center', gap: 8 }}>
                                ⚠️ Expired Documents & Insurance
                            </h2>
                            <button onClick={() => setShowExpiredModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: '#64748b' }}>&times;</button>
                        </div>
                        {expiredDrivers.length === 0 ? (
                            <p style={{ color: '#64748b' }}>No expired items found.</p>
                        ) : (
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                                <thead>
                                    <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Type</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>ID / Code</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Name / Details</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Expired Items</th>
                                        <th style={{ padding: '12px', borderBottom: '1px solid #e2e8f0', color: '#475569', fontWeight: 600 }}>Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {expiredDrivers.map(d => (
                                        <tr key={d._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                            <td style={{ padding: '12px' }}>
                                                <span style={{
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    padding: '3px 8px',
                                                    borderRadius: 6,
                                                    background: d.isClient ? '#fef3c7' : d.isVehicle ? '#e0f2fe' : '#ede9fe',
                                                    color: d.isClient ? '#b45309' : d.isVehicle ? '#0369a1' : '#6d28d9'
                                                }}>
                                                    {d.isClient ? 'Client' : d.isVehicle ? 'Vehicle' : 'Driver'}
                                                </span>
                                            </td>
                                            <td style={{ padding: '12px', fontWeight: 600, color: '#334155' }}>{d.displayId}</td>
                                            <td style={{ padding: '12px', color: '#334155', fontWeight: 600 }}>{d.displayName}</td>
                                            <td style={{ padding: '12px', color: '#ef4444', fontWeight: 600 }}>{d.expiredList.join(', ')}</td>
                                            <td style={{ padding: '12px' }}>
                                                <button 
                                                    onClick={() => {
                                                        setShowExpiredModal(false);
                                                        if (onNavigate) {
                                                             if (d.isClient) {
                                                                onNavigate('client-details', d._id);
                                                            } else {
                                                                onNavigate(d.isVehicle ? 'all-vehicles' : 'drivers', d._id);
                                                            }
                                                        }
                                                    }}
                                                    style={{ background: '#2563eb', color: 'white', border: 'none', padding: '5px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}

            {/* DRIVER DAILY LOGINS MODAL */}
            {showDriverModal && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20
                }} onClick={() => setShowDriverModal(false)}>
                    <div style={{
                        background: 'white', borderRadius: 20, width: '100%', maxWidth: 840,
                        maxHeight: '85vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)', border: '1px solid #e2e8f0',
                        fontFamily: "'Outfit', sans-serif"
                    }} onClick={e => e.stopPropagation()}>
                        
                        {/* Modal Header */}
                        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 40, height: 40, borderRadius: 12,
                                    background: driverModalTab === 'online' ? '#ecfdf5' : '#fffbeb',
                                    color: driverModalTab === 'online' ? '#059669' : '#d97706',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: `1px solid ${driverModalTab === 'online' ? '#a7f3d0' : '#fde68a'}`
                                }}>
                                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                                        <circle cx="9" cy="7" r="4" />
                                        {driverModalTab === 'online' ? (
                                            <polyline points="16 11 18 13 22 9" />
                                        ) : (
                                            <>
                                                <line x1="17" y1="8" x2="23" y2="14" />
                                                <line x1="23" y1="8" x2="17" y2="14" />
                                            </>
                                        )}
                                    </svg>
                                </div>
                                <div>
                                    <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>
                                        Today's Driver Daily Logins
                                    </h2>
                                    <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#64748b' }}>
                                        {driverDailyStatus.onlineCount} logged in &bull; {driverDailyStatus.notLoggedInCount} pending &bull; {driverDailyStatus.total} total active drivers
                                    </p>
                                </div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    onClick={loadDriverStatus}
                                    title="Refresh Status"
                                    style={{
                                        background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
                                        width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', color: '#64748b'
                                    }}
                                >
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="23 4 23 10 17 10" />
                                        <polyline points="1 20 1 14 7 14" />
                                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => setShowDriverModal(false)} 
                                    style={{ 
                                        background: 'white', border: '1px solid #e2e8f0', borderRadius: 10,
                                        width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', fontSize: 20, color: '#64748b', lineHeight: 1
                                    }}
                                >
                                    &times;
                                </button>
                            </div>
                        </div>

                        {/* Controls Bar: Tabs & Search */}
                        <div style={{ padding: '14px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, background: '#ffffff' }}>
                            <div style={{ display: 'flex', gap: 8 }}>
                                <button
                                    onClick={() => setDriverModalTab('online')}
                                    style={{
                                        padding: '8px 16px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                                        border: driverModalTab === 'online' ? '1px solid #10b981' : '1px solid #e2e8f0',
                                        background: driverModalTab === 'online' ? '#ecfdf5' : '#ffffff',
                                        color: driverModalTab === 'online' ? '#047857' : '#64748b',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                                    <span>Logged In Today</span>
                                    <span style={{
                                        background: driverModalTab === 'online' ? '#10b981' : '#f1f5f9',
                                        color: driverModalTab === 'online' ? '#ffffff' : '#64748b',
                                        fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 10
                                    }}>
                                        {driverDailyStatus.onlineCount}
                                    </span>
                                </button>

                                <button
                                    onClick={() => setDriverModalTab('notLoggedIn')}
                                    style={{
                                        padding: '8px 16px', borderRadius: 12, fontSize: 13, fontWeight: 700,
                                        border: driverModalTab === 'notLoggedIn' ? '1px solid #f59e0b' : '1px solid #e2e8f0',
                                        background: driverModalTab === 'notLoggedIn' ? '#fffbeb' : '#ffffff',
                                        color: driverModalTab === 'notLoggedIn' ? '#b45309' : '#64748b',
                                        cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b' }} />
                                    <span>Not Logged In Today</span>
                                    <span style={{
                                        background: driverModalTab === 'notLoggedIn' ? '#f59e0b' : '#f1f5f9',
                                        color: driverModalTab === 'notLoggedIn' ? '#ffffff' : '#64748b',
                                        fontSize: 11, fontWeight: 800, padding: '2px 7px', borderRadius: 10
                                    }}>
                                        {driverDailyStatus.notLoggedInCount}
                                    </span>
                                </button>
                            </div>

                            <div style={{ position: 'relative', width: 260 }}>
                                <input
                                    type="text"
                                    placeholder="Search name, code, phone..."
                                    value={driverSearchQuery}
                                    onChange={(e) => setDriverSearchQuery(e.target.value)}
                                    style={{
                                        width: '100%', padding: '8px 12px 8px 34px', borderRadius: 10,
                                        border: '1px solid #e2e8f0', fontSize: 13, outline: 'none',
                                        boxSizing: 'border-box', background: '#f8fafc'
                                    }}
                                />
                                <svg style={{ position: 'absolute', left: 10, top: 10, color: '#94a3b8' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8" />
                                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                </svg>
                                {driverSearchQuery && (
                                    <button 
                                        onClick={() => setDriverSearchQuery('')}
                                        style={{ position: 'absolute', right: 8, top: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 16 }}
                                    >
                                        &times;
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Table Content */}
                        <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
                            {(() => {
                                const list = (driverModalTab === 'online' ? driverDailyStatus.online : driverDailyStatus.notLoggedIn) || [];
                                const q = driverSearchQuery.trim().toLowerCase();
                                const filtered = q ? list.filter(d => 
                                    (d.name && d.name.toLowerCase().includes(q)) ||
                                    (d.driverCode && d.driverCode.toLowerCase().includes(q)) ||
                                    (d.phone && d.phone.toLowerCase().includes(q))
                                ) : list;

                                if (filtered.length === 0) {
                                    return (
                                        <div style={{ padding: '60px 20px', textAlign: 'center', color: '#94a3b8' }}>
                                            <div style={{ fontSize: 36, marginBottom: 8 }}>
                                                {driverModalTab === 'online' ? '🚕' : '🎉'}
                                            </div>
                                            <div style={{ fontSize: 15, fontWeight: 700, color: '#475569' }}>
                                                {driverModalTab === 'online' 
                                                    ? (driverSearchQuery ? 'No logged-in drivers match your search' : 'No drivers have logged in today yet') 
                                                    : (driverSearchQuery ? 'No matching drivers' : 'Awesome! All active drivers have logged in today!')}
                                            </div>
                                            {driverSearchQuery && (
                                                <button
                                                    onClick={() => setDriverSearchQuery('')}
                                                    style={{ marginTop: 12, background: 'none', border: '1px solid #cbd5e1', padding: '5px 14px', borderRadius: 8, fontSize: 12, color: '#16539C', cursor: 'pointer', fontWeight: 600 }}
                                                >
                                                    Clear Search
                                                </button>
                                            )}
                                        </div>
                                    );
                                }

                                return (
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ background: '#f8fafc', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>
                                                <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 700, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Driver ID / Code</th>
                                                <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 700, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Driver Name</th>
                                                <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 700, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Phone Number</th>
                                                <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 700, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status & Details</th>
                                                <th style={{ padding: '12px 20px', color: '#64748b', fontWeight: 700, fontSize: 11.5, textTransform: 'uppercase', letterSpacing: '0.04em', textAlign: 'right' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.map(driver => (
                                                <tr key={driver._id} style={{ borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' }}>
                                                    <td style={{ padding: '14px 20px' }}>
                                                        <span style={{
                                                            fontSize: 12, fontWeight: 800, padding: '4px 10px',
                                                            borderRadius: 8, background: '#f0fdf4', color: '#166534',
                                                            border: '1px solid #bbf7d0', display: 'inline-block'
                                                        }}>
                                                            #{driver.driverCode}
                                                        </span>
                                                    </td>
                                                    <td style={{ padding: '14px 20px' }}>
                                                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 13.5 }}>{driver.name}</div>
                                                        {isSuperadmin && driver.operatorName && (
                                                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{driver.operatorName}</div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '14px 20px', color: '#475569', fontWeight: 600 }}>
                                                        <a href={`tel:${driver.phone}`} style={{ color: '#0284c7', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                                                            {driver.phone}
                                                        </a>
                                                    </td>
                                                    <td style={{ padding: '14px 20px' }}>
                                                        {driverModalTab === 'online' ? (
                                                            <div>
                                                                <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                    fontSize: 11.5, fontWeight: 700, color: '#047857',
                                                                    background: '#ecfdf5', padding: '3px 8px', borderRadius: 6
                                                                }}>
                                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }} />
                                                                    Logged In
                                                                </span>
                                                                {driver.startOdometer > 0 && (
                                                                    <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 3 }}>
                                                                        Start Odometer: <strong>{driver.startOdometer} km</strong>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <div>
                                                                <span style={{
                                                                    display: 'inline-flex', alignItems: 'center', gap: 6,
                                                                    fontSize: 11.5, fontWeight: 700, color: '#b45309',
                                                                    background: '#fffbeb', padding: '3px 8px', borderRadius: 6
                                                                }}>
                                                                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#f59e0b' }} />
                                                                    Not Logged In Today
                                                                </span>
                                                                {driver.lastLoginAt ? (
                                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                                                                        Last login: {new Date(driver.lastLoginAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                                                    </div>
                                                                ) : (
                                                                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>No previous login recorded</div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ padding: '14px 20px', textAlign: 'right' }}>
                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                                                            <button
                                                                onClick={() => {
                                                                    setShowDriverModal(false);
                                                                    if (onNavigate) onNavigate('driver-logins', driver._id);
                                                                }}
                                                                style={{
                                                                    background: 'linear-gradient(135deg, #16539C 0%, #0F3A70 100%)',
                                                                    color: 'white', border: 'none', padding: '6px 12px',
                                                                    borderRadius: 8, fontSize: 12, fontWeight: 700,
                                                                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5,
                                                                    boxShadow: '0 2px 6px rgba(22, 83, 156, 0.25)'
                                                                }}
                                                                title="View Daily Logins"
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                                                    <line x1="16" y1="2" x2="16" y2="6" />
                                                                    <line x1="8" y1="2" x2="8" y2="6" />
                                                                    <line x1="3" y1="10" x2="21" y2="10" />
                                                                </svg>
                                                                Daily Logins
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setShowDriverModal(false);
                                                                    if (onNavigate) onNavigate('drivers', driver._id);
                                                                }}
                                                                style={{
                                                                    background: '#f8fafc', color: '#475569',
                                                                    border: '1px solid #cbd5e1', padding: '6px 10px',
                                                                    borderRadius: 8, fontSize: 12, fontWeight: 700,
                                                                    cursor: 'pointer'
                                                                }}
                                                                title="View Driver Profile"
                                                            >
                                                                Profile
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
